import { useId, useRef, type ReactNode } from 'react';

export function LockDialog({
  name,
  title,
  triggerLabel,
  triggerName,
  triggerClassName,
  trigger,
  children,
  handing = false,
}: {
  name: string;
  title: string;
  triggerLabel: string;
  triggerName: string;
  triggerClassName: string;
  trigger: ReactNode;
  children(close: () => void): ReactNode;
  handing?: boolean;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const opener = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const close = () => dialog.current?.close();

  return (
    <>
      <button
        ref={opener}
        type="button"
        data-name={triggerName}
        aria-label={triggerLabel}
        aria-haspopup="dialog"
        onClick={() => dialog.current?.showModal()}
        className={triggerClassName}
      >
        {trigger}
      </button>
      <dialog
        ref={dialog}
        data-name={`${name}-dialog`}
        aria-labelledby={titleId}
        aria-modal="true"
        onClose={() => opener.current?.focus()}
        onClick={(event) => {
          if (event.target === event.currentTarget) {
            close();
          }
        }}
        onKeyDown={(event) => {
          if (event.key !== 'Tab') {
            return;
          }
          const controls = Array.from(
            event.currentTarget.querySelectorAll<HTMLElement>(
              'button:not([disabled]), a[href], input:not([disabled]), [tabindex="0"]',
            ),
          );
          const first = controls[0];
          const last = controls.at(-1);
          if (event.shiftKey && document.activeElement === first && last) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last && first) {
            event.preventDefault();
            first.focus();
          }
        }}
        className={`fixed inset-0 m-auto max-h-[92dvh] w-[calc(100%-2rem)] overflow-y-auto rounded-2xl bg-white text-ink shadow-2xl backdrop:bg-black/60 backdrop:backdrop-blur-sm ${
          handing ? 'max-w-sm p-8' : 'max-w-md p-6 sm:p-8'
        }`}
      >
        <button
          type="button"
          data-name={`${name}-close`}
          aria-label="Close"
          onClick={close}
          className="absolute top-3 right-3 flex size-11 items-center justify-center rounded-full text-gray-500 focus-visible:outline-2 focus-visible:outline-progress"
        >
          <span className="flex size-8 items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200">
            <svg
              aria-hidden="true"
              className="size-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </span>
        </button>
        <h2
          id={titleId}
          className={
            handing
              ? 'mb-5 text-center text-xs font-semibold tracking-wider text-gray-400 uppercase'
              : 'pr-10 text-sm font-semibold text-gray-800'
          }
        >
          {title}
        </h2>
        {children(close)}
      </dialog>
    </>
  );
}
